import {
  init,
  add,
  track,
  setUserId,
  getUserId,
  setDeviceId,
  getDeviceId,
  getSessionId,
  identify,
  Identify,
  setOptOut,
} from '@amplitude/analytics-browser'
import invariant from 'tiny-invariant'
import { sanitizePaymentTelemetry } from 'utils/payment-return-url'
import * as Sentry from '@sentry/react'

import './cf-tl-event'
import {
  getAmplitudeTrackingState,
  isAmplitudeTrackingAllowed as isTrackingAllowed,
  setAmplitudeTrackingState,
} from './tracking-state'
import type { AmplitudeTrackingState } from './tracking-state'
import {
  clearEventQueue,
  enqueueEvent,
  processEventQueue,
  scheduleQueueDrain,
} from './events-queue'
import { getDesktopAnalyticsContext } from './desktop-context'
import { sessionStore } from 'utils/storage'
import { getCookie, setCookie } from 'utils/dom'
import { getCookieDomain } from 'utils/env'
import { COUNTRY_COOKIE_NAME, LANGUAGE_COOKIE_NAME } from 'constants/cookies'
import { languageMap } from 'cf-i18n/constants'
import type { Language } from 'codegen/types'

const AMPLITUDE_API_KEY =
  process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY ?? process.env.VITE_AMPLITUDE_API_KEY

invariant(
  AMPLITUDE_API_KEY,
  'VITE_AMPLITUDE_API_KEY or NEXT_PUBLIC_AMPLITUDE_API_KEY must be set within packages/analytics/amplitude/utils.ts',
)

const COOKIE_DOMAIN = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || process.env.VITE_COOKIE_DOMAIN

// Queue for storing user and device IDs when Amplitude is not available
let pendingUserId: string | null = null
let pendingDeviceId: string | null = null
let hasPendingUserId = false
let hasPendingDeviceId = false

export enum AmplitudeEvents {
  PAGE_VIEW = 'Loaded a Page',
  NOTIFICATION_CLICK = 'Web Notification Clicked',
  LOGIN_MODAL_OPENED = 'Opened Login Modal',
  REGISTRATION_MODAL_OPENED = 'Opened Register Modal',
  CLICKED_BANNER_ABOVE_NAV = 'Clicked Banner Above Nav',
  PRODUCT_VIEWED = 'Product Viewed',
  PRODUCT_LIST_VIEWED = 'Product List Viewed',
  DOWNLOADED_PRODUCT = 'Downloaded Product',
  PRODUCTS_SEARCHED = 'Products Searched',
  CLICKED_PAYMENT_MODAL = 'Clicked Payment Modal',
  VIEWED_PAYMENT_MODAL = 'Viewed Payment Modal',
  CLOSED_PAYMENT_MODAL = 'Closed Payment Modal',
  CLICKED_CONFIRMED_AND_PAID = 'Clicked Confirmed and Paid Payment Modal',
  SUBSCRIPTION_ALREADY_EXISTS = 'Subscription Already Exists',
  VIEWED_PAYMENT_BUTTON = 'Viewed Payment Button',
  CLICKED_PAYMENT_BUTTON = 'Clicked Payment Button',
  CANCELLED_PAYMENT_BUTTON = 'Cancelled Payment Button',
  CART_VIEWED = 'Cart Viewed',
  RECOMMENDATION_CLICKED = 'Recommendation overlay clicked',
  CLICKED_FLOW_BANNER = 'Clicked Flow Banner',
  EXPERIMENT_VIEWED = 'Experiment Viewed',
  CONFIRMATION_FLOW_COMPLETED = 'Confirmation Flow Completed',
  UPGRADE_BUTTON_CLICKED = 'Upgrade Button Clicked',
  LOAD_MORE_BUTTON_CLICKED = 'Load More Button Clicked',
  NO_RESULTS_FOUND_ON_SEARCH = 'No Results Found On Search',
  MAIN_MENU_STUDIO_BUTTON_CLICKED = 'Main Menu Studio Button Clicked',
  FREE_TRIAL_BUTTON_CLICKED = 'Clicked Free Trial Button',
  POST_REVIEW_REFERRAL_LINK_COPIED = 'Post Review Referral Link Copied',
  PROMO_POPUP_BUTTON_CLICKED = 'Promo Popup Button Clicked',
  PROMO_POPUP_VIEWED = 'Promo Popup Viewed',
  PROMO_POPUP_CLOSED = 'Promo Popup Closed',
  CLUBHOUSE_QUIZ_STARTED = 'Quiz Started',
  CLUBHOUSE_QUIZ_ENDED = 'Quiz Ended',
  JOINED_CLUBHOUSE = 'Clubhouse Joined',
  LEFT_CLUBHOUSE = 'Clubhouse Left',
  INVITE_STARTED = 'Clubhouse Invite Started',
  INVITE_SENT = 'Clubhouse Invite Sent',
  DISCUSSION_STARTED = 'Discussion Started',
  DISCUSSION_DRAFTED = 'Discussion Draft Opened',
  DISCUSSION_COMMENT_POSTED = 'Discussion Comment Posted',
  SEARCH_QUICK_PREVIEW_CLOSE = 'Search Quick Preview Closed',
  SEARCH_QUICK_PREVIEW_OPEN = 'Search Quick Preview Opened',
  SEARCH_QUICK_PREVIEW_BUTTON_CLICKED = 'Search Quick Preview Button Clicked',
  CLICKED_CANCEL_SUBSCRIPTION = 'Clicked Cancel Subscription',
  CLICKED_UNDO_CANCEL_SUBSCRIPTION = 'Clicked Undo Cancel Subscription',
  BANNER_VIEWED = 'Banner Viewed',
  HEADER_SEARCH_INPUT_CLICKED = 'Header Search Input Clicked',
  HEADER_SEARCH_INPUT_SEARCHED = 'Header Search Input Searched',
  SCROLLED_TO_TOP = 'Scrolled To Top',
  SEARCH_INPUT_CLICKED = 'Search Input Clicked',
  CLUBHOUSE_SWITCHER_CLICKED = 'Clubhouse Switcher Clicked',
  SUBSCRIPTION_AUTO_STARTED_SUCCESS = 'Subscription Auto-started Success',
  SUBSCRIPTION_AUTO_START_CLICKED = 'Subscription Auto-start Clicked',
  SUBSCRIPTION_AUTO_STARTED_FAILED = 'Subscription Auto-started Failed',
  COIN_PACK_PURCHASE_CLICKED = 'Coin Pack Purchase Clicked',
  PURCHASED_COIN_PACK = 'Purchased Coin Pack',
  STUDIO_AI_EXCLUSIVE_OFFER_SCREEN_VIEWED = 'Studio AI Exclusive Offer Screen Viewed',
  STUDIO_AI_EXCLUSIVE_OFFER_SCREEN_DISMISSED = 'Studio AI Exclusive Offer Screen Dismissed',
  STUDIO_AI_EXCLUSIVE_OFFER_SCREEN_UPGRADE_BUTTON_CLICKED = 'Studio AI Exclusive Offer Screen Upgrade Button Clicked',
  SHUFFLE_CLICKED = 'Shuffle Clicked',
  WELCOME_POPUP_VIEWED = 'Welcome Popup Viewed',
}

export enum AuthModalLocations {
  BANNER_ABOVE_NAV = 'banner above nav',
  SITE_HEADER = 'nav menu',
  LOGIN_MODAL = 'login modal',
  REGISTRATION_MODAL = 'registration modal',
  CART = 'cart',
  DESKTOP = 'desktop',
  BLACK_FRIDAY_WAITLIST = 'black friday waitlist',
}

let AMPLITUDE_INITIALIZED = false
let AMPLITUDE_INITIALIZING = false

const setTagLoaderOptOut = (optOut: boolean) => {
  if (typeof window === 'undefined') {
    return
  }

  const tagLoaderInstance = window.cfTLEvent?.ampInstance
  if (typeof tagLoaderInstance?.setOptOut === 'function') {
    tagLoaderInstance.setOptOut(optOut)
  }
}

const syncTagLoaderOptOut = () => {
  setTagLoaderOptOut(!isTrackingAllowed())
}

export const setAmplitudeTrackingAllowed = (state: AmplitudeTrackingState) => {
  setAmplitudeTrackingState(state)
  const allowed = isTrackingAllowed()
  setOptOut(!allowed)

  if (typeof window !== 'undefined') {
    document.removeEventListener('cfTrackingLoaded', syncTagLoaderOptOut)
    if (!allowed) {
      document.addEventListener('cfTrackingLoaded', syncTagLoaderOptOut)
    }
  }
  syncTagLoaderOptOut()

  if (state === false) {
    pendingUserId = null
    pendingDeviceId = null
    hasPendingUserId = false
    hasPendingDeviceId = false
    clearEventQueue()
  }

  if (isAmplitudeAvailable()) {
    schedulePendingUserDataFlush()
    scheduleQueueDrain()
  }
}

function safeParseInt(value: string | undefined) {
  const parsed = parseInt(value ?? '', 10)
  return isNaN(parsed) ? undefined : parsed
}

const flushPendingUserData = () => {
  if (!isTrackingAllowed()) {
    return
  }

  if (hasPendingUserId) {
    if (typeof window?.cfTLEvent?.ampInstance !== 'undefined') {
      window.cfTLEvent.ampInstance.setUserId(pendingUserId)
    } else {
      setUserId(pendingUserId ?? undefined)
    }
    pendingUserId = null
    hasPendingUserId = false
  }

  if (hasPendingDeviceId && pendingDeviceId !== null) {
    if (typeof window?.cfTLEvent?.ampInstance !== 'undefined') {
      window.cfTLEvent.ampInstance.setDeviceId(pendingDeviceId)
    } else {
      setDeviceId(pendingDeviceId)
    }
    pendingDeviceId = null
    hasPendingDeviceId = false
  }
}

// Defers pending identity updates (setUserId/setDeviceId) until Tag Loader has
// had a chance to attach, using the same poll → load → ceiling strategy as
// scheduleQueueDrain. If TL never loads (Playwright, routes without TL), falls
// back to the new SDK — which is the only SDK on those routes, so no split.
const schedulePendingUserDataFlush = () => {
  if (!hasPendingUserId && !hasPendingDeviceId) {
    return
  }
  if (typeof window === 'undefined') {
    return
  }

  // Fast path: TL already attached.
  if (window.cfTLEvent?.ampInstance) {
    flushPendingUserData()
    return
  }

  let flushed = false
  const flushOnce = () => {
    if (flushed) {
      return
    }
    flushed = true
    flushPendingUserData()
  }

  const pollId = window.setInterval(() => {
    if (typeof window === 'undefined') {
      return
    }
    if (flushed || window.cfTLEvent?.ampInstance) {
      window.clearInterval(pollId)
      flushOnce()
    }
  }, 100)

  const onLoadFallback = () => {
    if (typeof window === 'undefined') {
      return
    }
    window.clearInterval(pollId)
    flushOnce()
  }

  if (document.readyState === 'complete') {
    window.setTimeout(onLoadFallback, 0)
  } else {
    window.addEventListener('load', onLoadFallback, { once: true })
  }

  // Absolute ceiling so pending data is never stuck forever.
  window.setTimeout(onLoadFallback, 15_000)
}

const getLegacyAmpCookie = () => {
  if (typeof document === 'undefined') {
    return null
  }

  return (
    document.cookie
      .split(';')
      .map(c => c.trim())
      .find(c => c.startsWith('amp_'))
      ?.split('=')[1] || null
  )
}

const getAmpCookie = () => {
  if (typeof document === 'undefined') {
    return null
  }

  return (
    document.cookie
      .split(';')
      .map(c => c.trim())
      .find(c => c.startsWith('AMP_'))
      ?.split('=')[1] || null
  )
}

const decodeAmpCookie = (val: string | null) => {
  if (!val) {
    return null
  }

  try {
    return JSON.parse(decodeURIComponent(atob(val)))
  } catch {
    try {
      return JSON.parse(atob(val))
    } catch {
      return null
    }
  }
}

/**
 * Write a legacy-format `amp_<key>` cookie so amplitude-js (used by Tag Loader
 * on WordPress pages) picks up the same session on cross-page navigation.
 *
 * The new SDK stores its session in an `AMP_<10-char>` cookie, but the old
 * amplitude-js SDK reads `amp_<6-char>`. Without this bridge, navigating from
 * a new-SDK page to a WP page starts a new session.
 *
 * Cookie value is dot-separated (matches amplitude-js v5+ MetadataStorage):
 * deviceId . userId_b64 . optOut . sessionId_b32 . lastEventTime_b32 . eventId_b32 . identifyId_b32 . sequenceNumber_b32
 */
const syncLegacySessionCookie = () => {
  if (typeof window === 'undefined') {
    return
  }

  const legacyVal = getLegacyAmpCookie()

  // 1. If legacy exists → do nothing
  if (legacyVal) {
    return
  }

  const rawNew = getAmpCookie()
  if (!rawNew) {
    return
  }

  const decoded = decodeAmpCookie(rawNew)
  if (!decoded) {
    return
  }

  const legacyValue = [
    decoded.deviceId || '',
    '',
    decoded.optOut ? '1' : '',
    decoded.sessionId?.toString(32) || '',
    decoded.lastEventTime?.toString(32) || Date.now().toString(32),
    String(decoded.lastEventId ?? 0),
    '0',
    String(decoded.pageCounter ?? 0),
  ].join('.')

  setCookie({
    name: `amp_${AMPLITUDE_API_KEY.substring(0, 6)}`,
    value: legacyValue,
    expiresIn: 365 * 24 * 60 * 60 * 1000,
    domain: COOKIE_DOMAIN ? getCookieDomain(COOKIE_DOMAIN) : undefined,
  })
}

export interface AmplitudeAttributionOptions {
  /** Sent with `setOnce`, so the first value Amplitude stores for each key is never replaced. */
  firstTouchUserProperties: Record<string, string>
}

const getOwnDomainReferrers = () => {
  if (!COOKIE_DOMAIN) {
    return []
  }
  const domain = COOKIE_DOMAIN.replace(/^\./, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return [new RegExp(`(^|\\.)${domain}$`)]
}

// Any autocapture key left out counts as on in the SDK, so every other one is named and off.
const getAttributionOnlyAutocapture = () => ({
  attribution: {
    resetSessionOnNewCampaign: false,
    excludeReferrers: getOwnDomainReferrers(),
  },
  pageViews: false,
  sessions: false,
  formInteractions: false,
  fileDownloads: false,
  elementInteractions: false,
  frustrationInteractions: false,
  networkTracking: false,
  webVitals: false,
  pageUrlEnrichment: false,
})

const sendFirstTouchUserProperties = (properties: Record<string, string>) => {
  const firstTouch = new Identify()
  Object.entries(properties).forEach(([key, value]) => {
    firstTouch.setOnce(key, value)
  })
  identify(firstTouch)
}

const canInitializeAmplitude = () => {
  return (
    typeof window !== 'undefined' &&
    !AMPLITUDE_INITIALIZED &&
    !AMPLITUDE_INITIALIZING &&
    isTrackingAllowed()
  )
}

export const initAmplitude = async (
  deviceId: string | undefined,
  sessionId: string | undefined,
  attribution?: AmplitudeAttributionOptions,
) => {
  if (!canInitializeAmplitude()) {
    return
  }

  AMPLITUDE_INITIALIZING = true

  try {
    add({
      name: 'sanitize-payment-urls',
      type: 'enrichment',
      execute: event => Promise.resolve(sanitizePaymentTelemetry(event)),
    })
    await init(AMPLITUDE_API_KEY, {
      deviceId,
      sessionId: safeParseInt(sessionId),
      autocapture: attribution ? getAttributionOnlyAutocapture() : false,
      ...(getAmplitudeTrackingState() === undefined ? {} : { optOut: false }),
      // Prevent the new SDK from deleting the legacy amplitude-js cookies
      // on init. Tag Loader's ampInstance (amplitude-js) stores its session
      // in those cookies — deleting them forces TL to generate a new
      // session_id on every full page load, causing a session break.
      cookieOptions: { upgrade: false },
    } as const).promise

    AMPLITUDE_INITIALIZED = true

    if (!isTrackingAllowed()) {
      setOptOut(true)
      return
    }

    if (attribution) {
      sendFirstTouchUserProperties(attribution.firstTouchUserProperties)
    }
    syncLegacySessionCookie()
    schedulePendingUserDataFlush()
    // Drain any events that were queued before init completed. Prefers routing
    // through cfTLEvent.ampInstance (GROW-2704 session-consistency) and only
    // falls back to track() when Tag Loader never attaches (e.g. in Playwright).
    scheduleQueueDrain()
  } catch (error) {
    Sentry.captureException(new Error('Amplitude initialization failed'))
  } finally {
    AMPLITUDE_INITIALIZING = false
  }
}

const isAmplitudeAvailable = () => {
  return (
    typeof window !== 'undefined' &&
    isTrackingAllowed() &&
    (AMPLITUDE_INITIALIZED || typeof window?.cfTLEvent?.ampInstance !== 'undefined')
  )
}

//Function to generate or retrieve a unique tab ID
//We can use this to differentiate between multiple tabs opened by the same user and track tab-specific behavior
//TO NOTE: Some browsers will copy sessionStorage when duplicating tabs, so this ID might not be unique in those cases but it's still better than nothing
function getTabId() {
  const key = 'cf_tab_id'
  try {
    let tabId = sessionStore.getString(key)
    if (!tabId) {
      tabId = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`).toString()
      sessionStore.setString(key, tabId)
    }
    return tabId
    // oxlint-disable-next-line unicorn/catch-error-name
  } catch (er) {}

  return 'unknown'
}

const getAmplitudeEventContext = () => {
  const context: Record<string, unknown> = {}
  const sessionSource = sessionStore.getString('cf-studio-session-source')
  if (sessionSource) {
    context.studio_session_source = sessionSource
  }
  const desktopContext = getDesktopAnalyticsContext()
  if (desktopContext) {
    Object.assign(context, desktopContext)
  }
  return context
}

export const sendAmplitudeEvent = <T extends AmplitudeEvents | string>(
  eventName: T,
  eventProperties?: Record<string, unknown>,
  callback?: () => void,
) => {
  if (typeof window === 'undefined') {
    return
  }
  if (getAmplitudeTrackingState() === false) {
    callback?.()
    return
  }

  const defaultProperties: Record<string, unknown> = {
    path: window.location.pathname,
    referrer: document.referrer,
    search: window.location.search,
    title: document.title,
    url: window.location.href,
    user_agent_raw: navigator.userAgent,
    tab_id: getTabId(),
  }

  const properties = sanitizePaymentTelemetry({
    ...defaultProperties,
    ...getAmplitudeEventContext(),
    ...eventProperties,
  })

  // If Amplitude is not initialized, add event to queue
  if (!isAmplitudeAvailable()) {
    enqueueEvent(eventName, properties, callback)
    return
  }

  // Drain any queued events through this same routing path before sending the
  // current event. This ensures queued events (e.g. Experiment Viewed, which fires
  // before Tag Loader has loaded) use cfTLEvent.ampInstance when it is now available,
  // keeping them in the same Amplitude session as all other events.
  processEventQueue()

  // oxlint-disable-next-line no-negated-condition
  if (typeof window?.cfTLEvent?.ampInstance !== 'undefined') {
    window.cfTLEvent.ampInstance.logEvent(eventName, properties, callback)
  } else {
    track(eventName, properties)
    syncLegacySessionCookie()
    if (callback) {
      callback()
    }
  }
}

export const setAmplitudeUser = (userId: string | null) => {
  if (getAmplitudeTrackingState() === false) {
    return
  }

  if (!isAmplitudeAvailable()) {
    pendingUserId = userId
    hasPendingUserId = true
    return
  }
  // Prefer Tag Loader's instance so the identity update shares the same
  // session_id as tracked events. The new SDK's setUserId triggers
  // IdentityEventSender which sends an $identify with a different session_id.
  if (typeof window?.cfTLEvent?.ampInstance !== 'undefined') {
    window.cfTLEvent.ampInstance.setUserId(userId)
  } else {
    setUserId(userId ?? undefined)
  }
}

export const setAmplitudeUserProperties = () => {
  if (!isTrackingAllowed() || !isAmplitudeAvailable()) {
    return
  }

  const userCountry = getCookie(COUNTRY_COOKIE_NAME)
  const userLanguageCode = getCookie(LANGUAGE_COOKIE_NAME) as Language
  const userLanguage = userLanguageCode ? languageMap.get(userLanguageCode) : undefined

  // Prefer Tag Loader's instance so the identify call shares the same
  // session_id as tracked events. The new SDK's identify() sends an $identify
  // event with a different session_id, creating a second session in Amplitude.
  if (typeof window?.cfTLEvent?.ampInstance !== 'undefined') {
    const userProperties: Record<string, string> = {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
    if (userCountry) {
      userProperties.countryCf = userCountry
    }
    if (userLanguage) {
      userProperties.language = userLanguage
    }
    if (userLanguageCode) {
      userProperties.languageCode = userLanguageCode
    }
    window.cfTLEvent.ampInstance.setUserProperties(userProperties)
  } else {
    const identifyObj = new Identify()
    identifyObj.set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone)
    if (userCountry) {
      identifyObj.set('countryCf', userCountry)
    }
    if (userLanguage) {
      identifyObj.set('language', userLanguage)
    }
    if (userLanguageCode) {
      identifyObj.set('languageCode', userLanguageCode)
    }
    identify(identifyObj)
  }
}

export const getAmplitudeUser = () => {
  if (!isAmplitudeAvailable()) {
    return
  }
  if (typeof window?.cfTLEvent?.ampInstance !== 'undefined') {
    return window.cfTLEvent.ampInstance.getUserId()
  }
  return getUserId()
}

export const setAmplitudeDeviceId = (deviceId: string) => {
  if (getAmplitudeTrackingState() === false) {
    return
  }

  if (!isAmplitudeAvailable()) {
    pendingDeviceId = deviceId
    hasPendingDeviceId = true
    return
  }
  if (typeof window?.cfTLEvent?.ampInstance !== 'undefined') {
    window.cfTLEvent.ampInstance.setDeviceId(deviceId)
  } else {
    setDeviceId(deviceId)
  }
}

type AmplitudeProperty = string | number | string[] | number[]

export type AmplitudeLogEvent = (
  name: AmplitudeEvents,
  properties: Record<
    string,
    AmplitudeProperty | Record<string, AmplitudeProperty> | Record<string, AmplitudeProperty>[]
  >,
) => void

export const getAmplitudeSessionId = () => {
  if (!isAmplitudeAvailable()) {
    return
  }
  if (typeof window?.cfTLEvent?.ampInstance !== 'undefined') {
    return window.cfTLEvent.ampInstance.getSessionId()
  }
  return getSessionId()
}

export const trackPageView = (props: {
  banner_above_nav: string
  banner_above_nav_state: string
}) => {
  sendAmplitudeEvent(AmplitudeEvents.PAGE_VIEW, props)
}

export const trackLoginModalOpen = (location?: AuthModalLocations) => {
  sendAmplitudeEvent(AmplitudeEvents.LOGIN_MODAL_OPENED, {
    modal_type: 'login',
    ...(location && { location }),
  })
}

export const trackRegistrationModalOpen = (location?: AuthModalLocations) => {
  sendAmplitudeEvent(AmplitudeEvents.REGISTRATION_MODAL_OPENED, {
    modal_type: 'registration',
    ...(location && { location }),
  })
}

export const genRanHex = (size: number) =>
  [...Array(size)].map(() => Math.floor(Math.random() * 16).toString(16)).join('')

export const trackCartViewed = (eventProperties?: Record<string, unknown>) => {
  sendAmplitudeEvent(AmplitudeEvents.CART_VIEWED, {
    ...eventProperties,
    cart_id: eventProperties?.cart_id ?? `${genRanHex(32)}_${Date.now()}`,
  })
}

export const trackConfirmationFlowCompleted = () => {
  sendAmplitudeEvent(AmplitudeEvents.CONFIRMATION_FLOW_COMPLETED)
}
